<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { isoToDisplay } from '../domain/dateMask.js';
import { todayIso } from '../domain/birthDate.js';
import {
  DATE_MIN, DATE_MAX, isoToLocalDate, localDateToIso, isAllowedIso, calendarStart, addDaysIso, addMonthsIso, clampIso, parseIso,
} from '../domain/dateField.js';

/* Inhalt des Kalenderfensters von DateField: Vuetify-Kalender (VDatePicker) plus eigene Knöpfe.
   Die Auswahl eines Tages wird erst mit "OK" übernommen (Cancel verwirft); "Today" und "Clear" übernehmen sofort.
   Alle Umrechnungen Date ↔ Text laufen über domain/dateField.js (lokale Bestandteile, nie UTC). */
const props = defineProps({
  /** Aktueller Wert (ISO) oder ''. */
  iso: { type: String, default: '' },
  min: { type: String, default: DATE_MIN },
  max: { type: String, default: DATE_MAX },
  required: { type: Boolean, default: false },
  startYear: { type: Number, default: null },
});
const emit = defineEmits(['ok', 'cancel']);

const { t, locale } = useI18n();

const limits = computed(() => ({ min: props.min, max: props.max }));
const start = calendarStart({ iso: props.iso, startYear: props.startYear, min: props.min, max: props.max });
const pending = ref(parseIso(props.iso) ? props.iso : '');
const month = ref(start.month);
const year = ref(start.year);

const today = todayIso();
const todayAllowed = computed(() => isAllowedIso(today, limits.value));

const minDate = computed(() => isoToLocalDate(props.min));
const maxDate = computed(() => isoToLocalDate(props.max));
const pendingDate = computed(() => isoToLocalDate(pending.value));
const shown = computed(() => isoToDisplay(pending.value) || t('dateField.noneSelected'));

function onPick(date) {
  const iso = localDateToIso(date);
  if (iso && isAllowedIso(iso, limits.value)) pending.value = iso;
}

function goTo(iso) {
  const target = clampIso(iso, limits.value);
  pending.value = target;
  const p = parseIso(target);
  month.value = p[1] - 1;
  year.value = p[0];
  /* Fokus auf den neu gewählten Tag, damit weiter mit den Pfeilen navigiert werden kann */
  nextTick(() => root.value?.querySelector?.('.v-date-picker-month__day--selected button')?.focus());
}

const root = ref(null);

/* Tastatur: Pfeile ±1 Tag / ±7 Tage, Bild hoch/runter ±1 Monat (mit Shift ±1 Jahr), Enter = OK, Esc schließt (Menü/Dialog) */
function onKeydown(event) {
  const base = pending.value || clampIso(`${year.value}-${String(month.value + 1).padStart(2, '0')}-01`, limits.value);
  const moves = {
    ArrowLeft: () => addDaysIso(base, -1),
    ArrowRight: () => addDaysIso(base, 1),
    ArrowUp: () => addDaysIso(base, -7),
    ArrowDown: () => addDaysIso(base, 7),
    PageUp: () => addMonthsIso(base, event.shiftKey ? -12 : -1),
    PageDown: () => addMonthsIso(base, event.shiftKey ? 12 : 1),
  };
  if (moves[event.key]) {
    event.preventDefault();
    const next = moves[event.key]();
    if (next) goTo(next);
    return;
  }
  if (event.key === 'Enter' && !event.target.closest?.('button') && pending.value) {
    event.preventDefault();
    emit('ok', pending.value);
  }
}

defineExpose({ pending, goTo, onKeydown });
</script>

<template>
  <div
    ref="root"
    class="dpc"
    role="group"
    :aria-label="$t('dateField.calendar')"
    data-testid="date-picker-card"
    @keydown="onKeydown"
  >
    <div class="dpc-selected" data-testid="dpc-selected" aria-live="polite">{{ shown }}</div>
    <v-locale-provider :locale="locale">
      <v-date-picker
        :model-value="pendingDate"
        v-model:month="month"
        v-model:year="year"
        :min="minDate"
        :max="maxDate"
        :first-day-of-week="1"
        :show-adjacent-months="true"
        hide-header
        color="primary"
        width="100%"
        class="dpc-picker"
        @update:model-value="onPick"
      />
    </v-locale-provider>
    <div class="dpc-actions">
      <button v-if="todayAllowed" type="button" class="dpc-btn" data-testid="dpc-today" @click="emit('ok', today)">
        {{ $t('dateField.today') }}
      </button>
      <button v-if="!required" type="button" class="dpc-btn" data-testid="dpc-clear" @click="emit('ok', '')">
        {{ $t('dateField.clear') }}
      </button>
      <span class="dpc-spacer"></span>
      <button type="button" class="dpc-btn" data-testid="dpc-cancel" @click="emit('cancel')">{{ $t('common.cancel') }}</button>
      <button type="button" class="dpc-btn dpc-ok" data-testid="dpc-ok" :disabled="!pending" @click="emit('ok', pending)">
        {{ $t('dateField.ok') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.dpc {
  width: min(328px, calc(100vw - 24px));
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--panel);
  color: var(--text);
  box-shadow: 0 20px 60px var(--shadow-strong);
}
.dpc-selected {
  margin: 0 4px 6px;
  font-family: var(--display);
  font-size: 1.05rem;
  font-weight: 600;
}
.dpc-picker {
  background: transparent;
  box-shadow: none;
}
.dpc-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.dpc-spacer {
  flex: 1 1 auto;
}
.dpc-btn {
  min-height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--green);
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}
.dpc-btn:hover {
  background: var(--card-hover);
}
.dpc-btn:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 1px;
}
.dpc-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
